import axios from 'axios'
import {getApiErrorMessage} from '../../../shared/utils/apiErrorMessage'
import {appendIdParam, appendQueryParam, buildQueryString} from '../../../shared/utils/queryParams'
import {
  AssignedReports,
  Company,
  CompanyAdmin,
  CreatedReports,
  CustomAssignedReports,
  Fields,
  Leg,
  Reports,
  Submenu,
  Template,
  Vessel,
  Voyage,
  WarningLevel,
  VesselOperations,
  CargoOperation,
  CargoOperationRequest,
  CargoBreakupType,
  CargoFinalAttachment,
  ReportAttachment,
} from './_models'
import type {LibraryMutationContext} from './libraryApi'

const API_URL = process.env.REACT_APP_API_URL

const COMPANY_API_URL = `${API_URL}/users/company`
const VESSEL_API_URL = `${API_URL}/vessels`
const TEMPLATES_API_URL = `${API_URL}/templates`
const MENUS_API_URL = `${API_URL}/menus`
const SUBMENUS_API_URL = `${API_URL}/submenus`
const ASSIGN_API_URL = `${API_URL}/assignments`
const CUSTOM_API_URL = `${API_URL}/custom-templates`
const CREW_API_URL = `${API_URL}/crew`
const VOYAGES_API_URL = `${API_URL}/voyages`
const COMPANY_ADMIN_API_URL = `${API_URL}/company-group-admins`
const REPORTS_API_URL = `${API_URL}/reports`

const mutationQueryParams = (context?: LibraryMutationContext) => {
  if (!context) {
    return undefined
  }
  const params = new URLSearchParams()
  appendQueryParam(params, 'mode', context.mode)
  appendIdParam(params, 'companyGroupAdminId', context.companyGroupAdminId)
  appendIdParam(params, 'companyAdminId', context.companyAdminId)
  appendIdParam(params, 'vesselId', context.vesselId)
  appendQueryParam(params, 'vesselType', context.vesselType)
  return params.toString() ? params : undefined
}

export const buildAssignmentLookupUrl = (scopePath: string, companyAdminId?: unknown): string => {
  const params = new URLSearchParams()
  appendIdParam(params, 'caId', companyAdminId)
  const query = buildQueryString(params)
  return query ? `${scopePath}?${query}` : scopePath
}

export const getCreatedReportById = async (vesselId?: number): Promise<CreatedReports[]> => {
  try {
    const params = new URLSearchParams()
    appendIdParam(params, 'vesselId', vesselId)
    const response = await axios.get(API_URL + '/reports', {
      params: params.toString() ? params : undefined,
    })

    return response.data
  } catch (error: any) {
    console.error('Error fetching created reports:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching created reports'))
  }
}

export const getAssignedTemplatesForUser = async (): Promise<AssignedReports[]> => {
  try {
    const response = await axios.get(ASSIGN_API_URL + '/my-templates')

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching assigned reports'))
  }
}

export const getAllAssignments = async (): Promise<AssignedReports[]> => {
  try {
    const response = await axios.get(ASSIGN_API_URL)

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching assigned reports'))
  }
}

export const getAssignmentById = async (assignId: number): Promise<AssignedReports> => {
  try {
    const response = await axios.get(ASSIGN_API_URL + '/' + assignId)

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching assigned reports'))
  }
}

export const getAssignedTemplatesByCompanyAdmin = async (
  companyAdminId: number
): Promise<AssignedReports[]> => {
  try {
    const response = await axios.get(ASSIGN_API_URL + '/group/' + companyAdminId)

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports for company Admin:', error)
    throw new Error(
      getApiErrorMessage(error, 'Error fetching assigned reports for Company Admin')
    )
  }
}
export const getAssignedTemplatesByCompany = async (
  companyAdminId: number,
  companyId: number
): Promise<AssignedReports[]> => {
  try {
    const response = await axios.get(
      ASSIGN_API_URL + '/group/' + companyAdminId + '/company/' + companyId
    )

    console.log('Company assignments', response.data)

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports for company:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching assigned reports for company'))
  }
}

const normalizeAssignmentScopeId = (value: number, fieldName: string): number => {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${fieldName} is required`)
  }
  return Math.trunc(value)
}

const normalizeAssignmentVesselType = (vesselType: string): string => {
  const normalized = String(vesselType ?? '').trim()
  if (!normalized) {
    throw new Error('vesselType is required')
  }
  return normalized
}

export const getAssignedTemplatesByVesselType = async (
  companyAdminId: number,
  companyId: number | null | undefined,
  vesselType: string
): Promise<AssignedReports[]> => {
  try {
    const normalizedCompanyAdminId = normalizeAssignmentScopeId(
      companyAdminId,
      'companyAdminId'
    )
    const normalizedVesselType = normalizeAssignmentVesselType(vesselType)
    const url = buildAssignmentLookupUrl(
      `${ASSIGN_API_URL}/group/${normalizedCompanyAdminId}/vesselType/${normalizedVesselType}`,
      companyId
    )

    const response = await axios.get(url)

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports for vessel type:', error)
    throw new Error(
      error.response?.data?.message || 'Error fetching assigned reports for vessel type'
    )
  }
}
// export const getAssignedTemplatesByVesselType = async (companyAdminId: number, companyId: number, vesselType: string): Promise<AssignedReports[]> => {
//   try {
//     const response = await axios.get(ASSIGN_API_URL + "/group/" + companyAdminId + "/company/" + companyId + "/vesselType/" + vesselType);

//     return response.data;

//   } catch (error: any) {
//     console.error('Error fetching assigned reports for vessel type:', error);
//     throw new Error(error.response?.data?.message || 'Error fetching assigned reports for vessel type');
//   }
// };

export const getAssignedTemplatesByVesselId = async (
  companyAdminId: number,
  companyId: number,
  vesselType: string,
  vesselId: number
): Promise<AssignedReports[]> => {
  try {
    const url = buildAssignmentLookupUrl(
      `${ASSIGN_API_URL}/group/${companyAdminId}/vesselType/${vesselType}/vesselId/${vesselId}`,
      companyId
    )

    const response = await axios.get(url)

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports for vessel Id:', error)
    throw new Error(
      error.response?.data?.message || 'Error fetching assigned reports for vessel Id'
    )
  }
}

// export const getAssignedTemplatesByVesselId = async (companyAdminId: number, companyId: number, vesselType: string, vesselId: number): Promise<AssignedReports[]> => {
//   try {
//     const response = await axios.get(ASSIGN_API_URL + "/group/" + companyAdminId + "/" + companyId + "/" + vesselType + "/vesselId/" + vesselId);

//     return response.data;

//   } catch (error: any) {
//     console.error('Error fetching assigned reports for vessel Id:', error);
//     throw new Error(error.response?.data?.message || 'Error fetching assigned reports for vessel Id');
//   }
// };

// Without subcompany
export const getAssignedTemplatesByVessel = async (
  companyAdminId: number,
  companyId: number,
  vesselType: string,
  vesselId: number
): Promise<AssignedReports[]> => {
  try {
    const response = await axios.post(ASSIGN_API_URL + '/group/vesselId/' + vesselId, {
      companyGroupAdmin: companyAdminId,
    })

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports for vessel Id:', error)
    throw new Error(
      error.response?.data?.message || error.message || 'Error fetching assigned reports for vessel Id'
    )
  }
}

export const getAssignedTemplatesForCompany = async (
  companyAdminId: number,
  companyId: number
): Promise<AssignedReports[]> => {
  try {
    const response = await axios.get(
      ASSIGN_API_URL + '/company/null/' + companyAdminId + '/' + companyId
    )

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports for company:', error)
    throw new Error(error.response?.data?.message || 'Error fetching assigned reports for company')
  }
}

export const getAssignedTemplatesForVesselType = async (
  companyAdminId: number,
  companyId: number | null | undefined,
  vesselType: string
): Promise<AssignedReports[]> => {
  try {
    const url = buildAssignmentLookupUrl(
      `${ASSIGN_API_URL}/group/${companyAdminId}/vesselType/null/${vesselType}`,
      companyId
    )

    const response = await axios.get(url)

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports for vessel type:', error)
    throw new Error(
      error.response?.data?.message || error.message || 'Error fetching assigned reports for vessel type'
    )
  }
}

// export const getAssignedTemplatesForVesselType = async (companyAdminId: number, companyId: number, vesselType: string): Promise<AssignedReports[]> => {
//   try {
//     const response = await axios.get(ASSIGN_API_URL + "/group/" + companyAdminId + "/company/" + companyId + "/vesselType/null/" + vesselType);

//     return response.data;

//   } catch (error: any) {
//     console.error('Error fetching assigned reports for vessel type:', error);
//     throw new Error(error.response?.data?.message || 'Error fetching assigned reports for vessel type');
//   }
// };

export const getAssignedTemplatesForVesselId = async (
  companyAdminId: number,
  companyId: number | null | undefined,
  vesselType: string,
  vesselId: number
): Promise<AssignedReports[]> => {
  try {
    const url = buildAssignmentLookupUrl(
      `${ASSIGN_API_URL}/group/${companyAdminId}/vesselType/${vesselType}/vesselId/null/${vesselId}`,
      companyId
    )

    const response = await axios.get(url)

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports for vessel Id:', error)
    throw new Error(
      error.response?.data?.message || error.message || 'Error fetching assigned reports for vessel Id'
    )
  }
}

// export const getAssignedTemplatesForVesselId = async (companyAdminId: number, companyId: number, vesselType: string, vesselId: number): Promise<AssignedReports[]> => {
//   try {
//     const response = await axios.get(ASSIGN_API_URL + "/group/" + companyAdminId + "/" + companyId + "/" + vesselType + "/vesselId/null/" + vesselId);

//     return response.data;

//   } catch (error: any) {
//     console.error('Error fetching assigned reports for vessel Id:', error);
//     throw new Error(error.response?.data?.message || 'Error fetching assigned reports for vessel Id');
//   }
// };

export const getCustomAssignedTemplates = async (
  accessToken: string
): Promise<CustomAssignedReports[]> => {
  try {
    const response = await axios.get(TEMPLATES_API_URL + '/custom', {
      headers: {
        Authorization: `Bearer ${accessToken}`, // Attach the access token
      },
    })

    return response.data
  } catch (error: any) {
    console.error('Error fetching assigned reports:', error)
    throw new Error(error.response?.data?.message || 'Error fetching assigned reports')
  }
}

export const cloneTemplate = async (
  sourceId: number,
  newName: string,
  description: string,
  context: LibraryMutationContext
): Promise<CustomAssignedReports> => {
  try {
    const requestBody: any = {
      sourceId,
      masterTemplateId: sourceId,
      name: newName,
      description,
      companyGroupAdminId: context.companyGroupAdminId,
    }

    if (context.companyAdminId) {
      requestBody.companyAdminId = context.companyAdminId
    }
    if (context.vesselType) {
      requestBody.vesselType = context.vesselType
    }
    if (context.vesselId) {
      requestBody.vesselId = context.vesselId
    }
    if (process.env.NODE_ENV !== 'production') {
      console.debug('Operations library clone target', requestBody)
    }

    const response = await axios.post(`${TEMPLATES_API_URL}/clone`, requestBody)

    return response.data
  } catch (error: any) {
    console.error('Full error object:', error)
    throw new Error('Error cloning template')
  }
}

export const getVesselTypeList = async (): Promise<Vessel[]> => {
  try {
    const response = await axios.get(VESSEL_API_URL)

    return response.data
  } catch (error: any) {
    console.error('Error fetching vesel type list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessel type list')
  }
}

export const getCrewUnitById = async (crewId: number): Promise<any> => {
  try {
    const response = await axios.get(`${CREW_API_URL}/${crewId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching crew unit:', error)
    throw new Error(error.response?.data?.message || 'Error fetching crew unit')
  }
}

export const getCompanyList = async (): Promise<Company[]> => {
  try {
    const response = await axios.get(COMPANY_API_URL)

    return response.data // Return company data
  } catch (error: any) {
    console.error('Error fetching company list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company list')
  }
}

export const getTemplateList = async (): Promise<Template[]> => {
  try {
    const response = await axios.get(TEMPLATES_API_URL)

    let data = response.data

    return data
  } catch (error: any) {
    console.error('Error fetching template list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching template list')
  }
}

export const deleteTemplate = async (
  tempId: number,
  context?: LibraryMutationContext
): Promise<Fields[]> => {
  try {
    const response = await axios.delete(TEMPLATES_API_URL + '/delete/' + tempId, {
      params: mutationQueryParams(context),
    })

    return response.data
  } catch (error: any) {
    console.error('Error deleting template:', error)
    throw new Error(error.response?.data?.message || 'Error deleting template')
  }
}

export const getReportsForTemplate = async (tempId: number): Promise<Reports[]> => {
  try {
    const response = await axios.get(TEMPLATES_API_URL + '/' + tempId + '/menus')

    let data = response.data

    console.log('menu response data: ', data)
    return data
  } catch (error: any) {
    console.error('Error fetching reports list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching reports list')
  }
}

export const getTabsForReport = async (menuId: number): Promise<Submenu[]> => {
  try {
    const response = await axios.get(MENUS_API_URL + '/' + menuId + '/submenus')

    let data = response.data

    return data
  } catch (error: any) {
    console.error('Error fetching tabs list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching tabs list')
  }
}

export const getFieldsForTab = async (tabId: number): Promise<Fields[]> => {
  try {
    const response = await axios.get(SUBMENUS_API_URL + '/' + tabId + '/fields')

    let data = response.data

    const fields: Fields[] = data.map((field: any) => ({
      ...field,
      submenu: {id: tabId},
    }))

    return fields
  } catch (error: any) {
    console.error('Error fetching fields list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching fields list')
  }
}

export const createTemplate = async (templateName: string) => {
  try {
    const response = await axios.post(TEMPLATES_API_URL + '/create', {
      name: templateName,
      isActive: true,
    })

    return response.data
  } catch (error: any) {
    console.error('Error creating template:', error)
    throw new Error(error.response?.data?.message || 'Error creating template')
  }
}

export const createMenuItem = async (
  tempId: number,
  reportName: string,
  active: boolean,
  order?: number
): Promise<Reports[]> => {
  try {
    const response = await axios.post(TEMPLATES_API_URL + '/' + tempId + '/menus/create', {
      name: reportName,
      isActive: active,
    })

    return response.data
  } catch (error: any) {
    console.error('Error creating menu item:', error)
    throw new Error(error.response?.data?.message || 'Error creating menu item')
  }
}

export const updateMenuItem = async (
  tempId: number,
  reportId: number,
  reportName: string,
  active: boolean,
  order?: number,
  context?: LibraryMutationContext
): Promise<Reports[]> => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Operations Library] updateMenuItem mutation', {
        templateId: tempId,
        menuId: reportId,
        payload: {name: reportName, isActive: active},
        context,
      })
    }
    const response = await axios.put(
      TEMPLATES_API_URL + '/' + tempId + '/menus/update/' + reportId,
      {
        name: reportName,
        isActive: active,
      },
      { params: mutationQueryParams(context) }
    )

    return response.data
  } catch (error: any) {
    console.error('Error updating menu item:', error)
    throw new Error(error.response?.data?.message || 'Error updating menu item')
  }
}

export const deleteMenuItem = async (tempId: number, reportId: number): Promise<Reports[]> => {
  try {
    const response = await axios.delete(
      TEMPLATES_API_URL + '/' + tempId + '/menus/delete/' + reportId
    )

    return response.data
  } catch (error: any) {
    console.error('Error deleting menu item:', error)
    throw new Error(error.response?.data?.message || 'Error deleting menu item')
  }
}

export const createCustomMenuItem = async (
  accessToken: string,
  customTempId: number,
  reportName: string,
  active: boolean,
  order?: number
): Promise<Reports[]> => {
  try {
    const response = await axios.post(
      CUSTOM_API_URL + '/' + customTempId + '/menu/create',
      {
        name: reportName,
        active: active,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`, // Attach the access token
        },
      }
    )

    return response.data
  } catch (error: any) {
    console.error('Error creating menu item:', error)
    throw new Error(error.response?.data?.message || 'Error creating menu item')
  }
}

export const createCustomSubmenuItem = async (
  accessToken: string,
  menuId: number,
  reportName: string,
  active: boolean,
  order?: number
): Promise<Submenu[]> => {
  try {
    const response = await axios.post(
      API_URL + '/menus' + menuId + '/submenus/customTemplates/create',
      {
        name: reportName,
        active: active,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`, // Attach the access token
        },
      }
    )

    return response.data
  } catch (error: any) {
    console.error('Error creating submenu item:', error)
    throw new Error(error.response?.data?.message || 'Error creating submenu item')
  }
}

export const createCustomFieldItem = async (
  accessToken: string,
  submenuId: number,
  label: string,
  fieldType: string,
  required: boolean,
  active?: boolean,
  warningOnly?: WarningLevel | undefined,
  unit?: string,
  minValue?: number,
  maxValue?: number,
  options?: string[] | null
): Promise<Submenu[]> => {
  try {
    const response = await axios.post(
      API_URL + '/submenus' + submenuId + '/fields/customTemplate/create',
      {
        label,
        fieldType,
        required,
        active: active,
        unit,
        minValue,
        maxValue,
        warningOnly,
        optionsJson: options,
        submenu: {
          id: submenuId,
        },
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`, // Attach the access token
        },
      }
    )

    return response.data
  } catch (error: any) {
    console.error('Error creating submenu item:', error)
    throw new Error(error.response?.data?.message || 'Error creating submenu item')
  }
}

export const createSubmenuItem = async (
  menuId: number,
  reportName: string,
  active: boolean,
  order?: number
): Promise<Submenu> => {
  try {
    const payload: any = {
      name: reportName,
      isActive: active,
    }

    if (order !== undefined) {
      payload.order = order
    }

    const response = await axios.post(MENUS_API_URL + '/' + menuId + '/submenus/create', payload)

    return response.data
  } catch (error: any) {
    console.error('Error creating submenu item:', error)
    throw new Error(getApiErrorMessage(error, 'Error creating submenu item'))
  }
}

export const updateSubMenuItem = async (
  menuId: number,
  submenuId: number,
  tabName: string,
  active: boolean,
  order?: number,
  context?: LibraryMutationContext
): Promise<Reports[]> => {
  try {
    const response = await axios.put(
      MENUS_API_URL + '/' + menuId + '/submenus/update/' + submenuId,
      {
        name: tabName,
        isActive: active,
      },
      { params: mutationQueryParams(context) }
    )

    return response.data
  } catch (error: any) {
    console.error('Error updating menu item:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating menu item'))
  }
}

export const deleteSubMenuItem = async (menuId: number, submenuId: number): Promise<Reports[]> => {
  try {
    const response = await axios.delete(
      MENUS_API_URL + '/' + menuId + '/submenus/delete/' + submenuId
    )

    return response.data
  } catch (error: any) {
    console.error('Error deleting menu item:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting menu item'))
  }
}

export const createField = async (
  submenuId: number,
  label: string,
  fieldType: string,
  readOnly: boolean,
  required: boolean,
  active?: boolean,
  warningOnly?: WarningLevel | undefined,
  unit?: string,
  minValue?: number,
  maxValue?: number,
  options?: string[] | null
): Promise<Fields[]> => {
  try {
    const response = await axios.post(SUBMENUS_API_URL + '/' + submenuId + '/fields/create', {
      label,
      fieldType,
      required,
      readOnly,
      isActive: active,
      unit,
      minValue,
      maxValue,
      warningOnly,
      optionsJson: options,
      submenu: {
        id: submenuId,
      },
    })

    return response.data
  } catch (error: any) {
    console.error('Error creating submenu item:', error)
    throw new Error(getApiErrorMessage(error, 'Error creating Field item'))
  }
}

export const updateField = async (
  submenuId: number,
  fieldId: number,
  label: string,
  fieldType: string,
  required?: boolean,
  readOnly?: boolean,
  warningOnly?: WarningLevel | undefined,
  active?: boolean,
  unit?: string,
  minValue?: number,
  maxValue?: number,
  options?: string[],
  order?: number,
  context?: LibraryMutationContext
): Promise<Fields[]> => {
  try {
    const payload: any = {
      label,
      fieldType,
      readOnly,
      required,
      isActive: active,
      unit,
      minValue,
      maxValue,
      warningOnly,
      submenu: {
        id: submenuId,
      },
    }

    if (order !== undefined) {
      payload.order = order
    }

    if (options && options.length > 0) {
      payload.optionsJson = options
    }

    const response = await axios.put(
      `${SUBMENUS_API_URL}/${submenuId}/fields/update/${fieldId}`,
      payload,
      { params: mutationQueryParams(context) }
    )

    return response.data
  } catch (error: any) {
    console.error('Error updating field item:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating field item'))
  }
}

export const deleteField = async (submenuId: number, fieldId: number): Promise<Fields[]> => {
  try {
    const response = await axios.delete(
      SUBMENUS_API_URL + '/' + submenuId + '/fields/delete/' + fieldId
    )

    return response.data
  } catch (error: any) {
    console.error('Error deleting field item:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting field item'))
  }
}
export const deleteAssignment = async (assgnId: number): Promise<AssignedReports[]> => {
  try {
    const response = await axios.delete(ASSIGN_API_URL + '/remove/' + assgnId)

    return response.data
  } catch (error: any) {
    console.error('Error deleting assignment:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting assignment'))
  }
}

export const assignReport = async (
  templateId: number,
  context: LibraryMutationContext
): Promise<AssignedReports[]> => {
  try {
    if (!context.companyGroupAdminId) {
      throw new Error('companyGroupAdminId is required for assignment')
    }
    const requestBody: any = {
      template: {
        id: templateId,
      },
    }

    if (context.mode) {
      requestBody.mode = context.mode
    }
    if (context.companyGroupAdminId) {
      requestBody.companyGroupAdminId = context.companyGroupAdminId
    }
    if (context.companyAdminId) {
      requestBody.companyAdmin = context.companyAdminId
      requestBody.companyAdminId = context.companyAdminId
    }
    if (context.vesselType) {
      requestBody.vesselType = context.vesselType
    }
    if (context.vesselId) {
      requestBody.vesselId = context.vesselId
    }
    if (process.env.NODE_ENV !== 'production') {
      console.debug('Operations library assign target', requestBody)
    }

    const response = await axios.post(
      ASSIGN_API_URL + '/assign/group/' + context.companyGroupAdminId,
      requestBody
    )

    return response.data
  } catch (error: any) {
    console.error('Error assigning template:', error)
    throw new Error(getApiErrorMessage(error, 'Error assigning template'))
  }
}

export const assignCustomTemplate = async (
  customTemplateId: number,
  context: LibraryMutationContext
): Promise<AssignedReports[]> => {
  try {
    if (!context.companyGroupAdminId) {
      throw new Error('companyGroupAdminId is required for custom assignment')
    }
    const requestBody: any = {
      customTemplate: {
        id: customTemplateId,
      },
      mode: context.mode,
      companyGroupAdminId: context.companyGroupAdminId,
    }

    if (context.companyAdminId) {
      requestBody.companyAdmin = context.companyAdminId
      requestBody.companyAdminId = context.companyAdminId
    }
    if (context.vesselType) {
      requestBody.vesselType = context.vesselType
    }
    if (context.vesselId) {
      requestBody.vesselId = context.vesselId
    }
    if (process.env.NODE_ENV !== 'production') {
      console.debug('Operations library assign custom target', requestBody)
    }

    const response = await axios.post(
      ASSIGN_API_URL + '/assign/custom/group/' + context.companyGroupAdminId,
      requestBody
    )

    return response.data
  } catch (error: any) {
    console.error('Error assigning custom template:', error)
    throw new Error(getApiErrorMessage(error, 'Error assigning custom template'))
  }
}

export const updateCustomMenuItem = async (
  customTempId: number,
  reportId: number,
  reportName: string,
  active: boolean,
  order?: number,
  context?: LibraryMutationContext
): Promise<Reports[]> => {
  try {
    const response = await axios.put(CUSTOM_API_URL + '/' + customTempId + '/menus/' + reportId, {
      name: reportName,
      isActive: active,
    }, { params: mutationQueryParams(context) })

    return response.data
  } catch (error: any) {
    console.error('Error updating custom menu item:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating custom menu item'))
  }
}

export const updateCustomSubMenuItem = async (
  customTempId: number,
  submenuId: number,
  tabName: string,
  active: boolean,
  order?: number,
  context?: LibraryMutationContext
): Promise<Reports[]> => {
  try {
    const response = await axios.put(
      CUSTOM_API_URL + '/' + customTempId + '/submenus/' + submenuId,
      {
        name: tabName,
        isActive: active,
      },
      { params: mutationQueryParams(context) }
    )

    return response.data
  } catch (error: any) {
    console.error('Error updating custom submenu item:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating custom submenu item'))
  }
}

export const updateCustomField = async (
  customTempId: number,
  fieldId: number,
  label: string,
  fieldType: string,
  readOnly: boolean,
  required?: boolean,
  warningOnly?: WarningLevel | undefined,
  active?: boolean,
  unit?: string,
  minValue?: number,
  maxValue?: number,
  options?: string[],
  order?: number,
  context?: LibraryMutationContext
): Promise<Fields[]> => {
  try {
    const payload: any = {
      label,
      fieldType,
      required,
      readOnly,
      optionsJson: options,
      isActive: active,
      unit,
      minValue,
      maxValue,
      warningOnly,
    }

    if (order !== undefined) {
      payload.order = order
    }

    if (options && options.length > 0) {
      payload.optionsJson = options
    }

    const response = await axios.put(`${CUSTOM_API_URL}/${customTempId}/fields/${fieldId}`, payload, {
      params: mutationQueryParams(context),
    })

    return response.data
  } catch (error: any) {
    console.error('Error updating custom field item:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating custom field item'))
  }
}

export const submitReport = async (
  reportType: string,
  status: string,
  hasWarnings: boolean,
  voyageId: number,
  assignmentId: number,
  values: {field: number; valueText: string}[],
  vesselId?: number,
  noOfWarnings?: number,
  hasRemarks?: boolean,
  remarks?: string,
  positionLat?: string,
  positionLon?: string,
  latLongSource?: string,
  dataSource?: string,
  legId?: number
): Promise<CreatedReports[]> => {
  try {
    const effectiveHasReview = Boolean(hasRemarks ?? (remarks && String(remarks).trim() !== ''))
    const sanitizedValues = Array.isArray(values)
      ? values.filter((v: any) => {
          const fieldId = Number(v?.field)
          return Number.isFinite(fieldId) && fieldId > 0 && fieldId < 90000
        })
      : values
    const response = await axios.post(CREW_API_URL + '/' + assignmentId + '/reports/submitReport', {
      reportType,
      status,
      submittedDateTime: new Date().toISOString(), // current timestamp
      createdDateTime: new Date().toISOString(), // current timestamp
      hasWarnings,
      voyage: voyageId,
      vesselId: vesselId,
      legId: legId,
      values: sanitizedValues,
      noOfWarnings,
      hasReview: effectiveHasReview,
      remark: remarks,
      positionLat,
      positionLon,
      latitude: positionLat,
      longitude: positionLon,
      latLongSource,
      dataSource,
    })

    return response.data
  } catch (error: any) {
    console.error('Error submitting report:', error)
    throw new Error(getApiErrorMessage(error, 'Error submitting report'))
  }
}

export const updatedSubmittedReport = async (
  reportId: number,
  reportType: string,
  status: string,
  hasWarnings: boolean,
  voyageId: number,
  assignmentId: number,
  values: {field: number; valueText: string}[],
  vesselId?: number,
  noOfWarnings?: number,
  hasRemarks?: boolean,
  remarks?: string,
  positionLat?: string,
  positionLon?: string,
  latLongSource?: string,
  dataSource?: string,
  legId?: number
): Promise<CreatedReports[]> => {
  try {
    const effectiveHasReview = Boolean(hasRemarks ?? (remarks && String(remarks).trim() !== ''))
    const sanitizedValues = Array.isArray(values)
      ? values.filter((v: any) => {
          const fieldId = Number(v?.field)
          return Number.isFinite(fieldId) && fieldId > 0 && fieldId < 90000
        })
      : values
    const response = await axios.put(API_URL + '/reports/update/' + reportId, {
      reportType,
      status,
      submittedDateTime: new Date().toISOString(), // current timestamp
      hasWarnings,
      voyage: voyageId,
      vesselId: vesselId,
      legId: legId,
      values: sanitizedValues,
      noOfWarnings,
      hasReview: effectiveHasReview,
      remark: remarks,
      positionLat,
      positionLon,
      latitude: positionLat,
      longitude: positionLon,
      latLongSource,
      dataSource,
    })

    return response.data
  } catch (error: any) {
    console.error('Error submitting report:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating submitted report'))
  }
}

export const deleteDraftReport = async (reportId: number): Promise<CreatedReports[]> => {
  try {
    const response = await axios.delete(API_URL + '/reports/delete/' + reportId)

    return response.data
  } catch (error: any) {
    console.error('Error deleting draft report:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting draft report'))
  }
}

export const listReportAttachments = async (reportId: number): Promise<ReportAttachment[]> => {
  try {
    const response = await axios.get<ReportAttachment[]>(`${REPORTS_API_URL}/${reportId}/attachments`)
    return response.data
  } catch (error: any) {
    console.warn('Error listing report attachments:', error)
    return []
  }
}

export const uploadReportAttachment = async (
  reportId: number,
  file: File,
  documentName?: string,
  remarks?: string
): Promise<ReportAttachment> => {
  try {
    const formData = new FormData()
    if (documentName) formData.append('documentName', documentName)
    if (remarks) formData.append('remarks', remarks)
    formData.append('file', file)

    const response = await axios.post<ReportAttachment>(
      `${REPORTS_API_URL}/${reportId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )

    return response.data
  } catch (error: any) {
    console.error('Error uploading report attachment:', error)
    throw new Error(getApiErrorMessage(error, 'Error uploading report attachment'))
  }
}

export const deleteReportAttachment = async (reportId: number, attachmentId: number): Promise<void> => {
  try {
    await axios.delete(`${REPORTS_API_URL}/${reportId}/attachments/${attachmentId}`)
  } catch (error: any) {
    console.error('Error deleting report attachment:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting report attachment'))
  }
}

export const reportAttachmentViewUrl = (reportId: number, attachmentId: number) =>
  `${REPORTS_API_URL}/${reportId}/attachments/${attachmentId}/view`

export const reportAttachmentDownloadUrl = (reportId: number, attachmentId: number) =>
  `${REPORTS_API_URL}/${reportId}/attachments/${attachmentId}/download`

export const fetchReportAttachmentViewBlob = async (
  reportId: number,
  attachmentId: number
): Promise<Blob> => {
  try {
    const response = await axios.get(
      `${REPORTS_API_URL}/${reportId}/attachments/${attachmentId}/view`,
      {responseType: 'blob'}
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching report attachment for view:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching report attachment'))
  }
}

export const fetchReportAttachmentDownloadBlob = async (
  reportId: number,
  attachmentId: number
): Promise<Blob> => {
  try {
    const response = await axios.get(
      `${REPORTS_API_URL}/${reportId}/attachments/${attachmentId}/download`,
      {responseType: 'blob'}
    )
    return response.data
  } catch (error: any) {
    console.error('Error fetching report attachment for download:', error)
    throw new Error(getApiErrorMessage(error, 'Error downloading report attachment'))
  }
}

export const getVoyageList = async (): Promise<Voyage[]> => {
  try {
    const response = await axios.get(VOYAGES_API_URL)

    return response.data
  } catch (error: any) {
    console.error('Error fetching voyage list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching voyage list')
  }
}

export const getVoyagesByVessel = async (vesselId: number): Promise<Voyage[]> => {
  try {
    const response = await axios.get(`${VOYAGES_API_URL}/vessels/${vesselId}`)
    const directList: Voyage[] = Array.isArray(response.data) ? response.data : []
    if (directList.length > 0) {
      return directList
    }

    // Fallback: some deployments return empty for vessel endpoint while list API has data.
    const allVoyages = await getVoyageList()
    return allVoyages.filter((v: any) => Number(v?.vessel?.id) === Number(vesselId))
  } catch (error: any) {
    console.error('Error fetching voyages by vessel:', error)
    try {
      const allVoyages = await getVoyageList()
      return allVoyages.filter((v: any) => Number(v?.vessel?.id) === Number(vesselId))
    } catch {
      throw new Error(error.response?.data?.message || 'Error fetching voyages by vessel')
    }
  }
}

export const getVoyageNumber = async (vesselId: number) => {
  try {
    const response = await axios.get(`${VOYAGES_API_URL}/next-number/${vesselId}`)

    return response.data
  } catch (error: any) {
    console.error('Error fetching next voyage number:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching next voyage number'))
  }
}

export const createVoyage = async (
  voyageNumber: string,
  departurePort: string,
  arrivalPort: string | undefined | null,
  startDate: string,
  endDate: string | undefined,
  voyageRoute: string,
  vesselId: number,
  companyGroupAdminId: number | undefined,
  charteredVoyageNumber?: string,
  status?: string,
  securityLevel?: string,
  iceVoyage?: boolean,
  eca?: boolean,
  etd?: string,
  cosp?: string,
  eosp?: string,
  arrivalFew?: string,
  eta?: string,
  estimatedDistance?: string,
  displacement?: string,
  draftFore?: string,
  draftMid?: string,
  draftAft?: string,
  totalCargoOnboard?: string,
  loadingCondition?: string,
  chartererName?: string,
  chartererNo?: string,
  cpSpeed?: string,
  cpConsumptionTotal?: string,
  agentName?: string,
  agentContactNumber?: string,
  agentTelephoneNumber?: string,
  agentFaxAddress?: string,
  agentEmail?: string,
  companyAdminId?: number
) => {
  try {
    const requestBody: any = {
      voyageNumber,
      departurePort,
      arrivalPort: arrivalPort || undefined,
      startDate,
      endDate: endDate || undefined,
      voyageRoute,
      companyGroupAdminId,
      companyAdminId,
      vessel: {id: vesselId},
    }

    if (iceVoyage !== undefined) requestBody.iceVoyage = iceVoyage
    if (charteredVoyageNumber?.trim()) requestBody.charteredVoyageNumber = charteredVoyageNumber.trim()
    if (status?.trim()) requestBody.status = status.trim()
    if (securityLevel?.trim()) requestBody.securityLevel = securityLevel.trim()
    if (eca !== undefined) requestBody.eca = eca
    if (etd?.trim()) requestBody.etd = etd.trim()
    if (cosp?.trim()) requestBody.cosp = cosp.trim()
    if (eosp?.trim()) requestBody.eosp = eosp.trim()
    if (arrivalFew?.trim()) requestBody.arrivalFew = arrivalFew.trim()
    if (eta?.trim()) requestBody.eta = eta.trim()
    if (estimatedDistance && !isNaN(parseFloat(estimatedDistance))) requestBody.estimatedDistance = parseFloat(estimatedDistance)
    if (displacement && !isNaN(parseFloat(displacement))) requestBody.displacement = parseFloat(displacement)
    if (draftFore && !isNaN(parseFloat(draftFore))) requestBody.draftFore = parseFloat(draftFore)
    if (draftMid && !isNaN(parseFloat(draftMid))) requestBody.draftMid = parseFloat(draftMid)
    if (draftAft && !isNaN(parseFloat(draftAft))) requestBody.draftAft = parseFloat(draftAft)
    if (totalCargoOnboard && !isNaN(parseFloat(totalCargoOnboard))) requestBody.totalCargoOnboard = parseFloat(totalCargoOnboard)
    if (loadingCondition?.trim()) requestBody.loadingCondition = loadingCondition.trim()
    if (chartererName?.trim()) requestBody.chartererName = chartererName.trim()
    if (chartererNo?.trim()) requestBody.chartererNo = chartererNo.trim()
    if (cpSpeed && !isNaN(parseFloat(cpSpeed))) requestBody.cpSpeed = parseFloat(cpSpeed)
    if (cpConsumptionTotal && !isNaN(parseFloat(cpConsumptionTotal))) requestBody.cpConsumptionTotal = parseFloat(cpConsumptionTotal)
    if (agentName?.trim()) requestBody.agentName = agentName.trim()
    if (agentContactNumber?.trim()) requestBody.agentContactNumber = agentContactNumber.trim()
    if (agentTelephoneNumber?.trim()) requestBody.agentTelephoneNumber = agentTelephoneNumber.trim()
    if (agentFaxAddress?.trim()) requestBody.agentFaxAddress = agentFaxAddress.trim()
    if (agentEmail?.trim()) requestBody.agentEmail = agentEmail.trim()

    const response = await axios.post(`${VOYAGES_API_URL}/create`, requestBody)

    return response.data
  } catch (error: any) {
    console.error('Error creating voyage:', error)
    throw new Error(getApiErrorMessage(error, 'Error creating voyage'))
  }
}

export const updateVoyage = async (
  voyageId: number,
  voyageNumber: string,
  departurePort: string,
  arrivalPort: string | undefined | null,
  startDate: string,
  endDate: string | undefined,
  voyageRoute: string,
  active: boolean,
  charteredVoyageNumber?: string,
  status?: string,
  securityLevel?: string,
  iceVoyage?: boolean,
  eca?: boolean,
  etd?: string,
  cosp?: string,
  eosp?: string,
  arrivalFew?: string,
  eta?: string,
  estimatedDistance?: string,
  displacement?: string,
  draftFore?: string,
  draftMid?: string,
  draftAft?: string,
  totalCargoOnboard?: string,
  loadingCondition?: string,
  chartererName?: string,
  chartererNo?: string,
  cpSpeed?: string,
  cpConsumptionTotal?: string,
  agentName?: string,
  agentContactNumber?: string,
  agentTelephoneNumber?: string,
  agentFaxAddress?: string,
  agentEmail?: string
) => {
  try {
    const requestBody: any = {
      voyageNumber,
      departurePort,
      arrivalPort: arrivalPort || undefined,
      startDate,
      endDate: endDate || undefined,
      voyageRoute,
      isActive: active,
    }

    if (charteredVoyageNumber?.trim()) requestBody.charteredVoyageNumber = charteredVoyageNumber.trim()
    if (status !== undefined) requestBody.status = status?.trim() || null
    if (securityLevel !== undefined) requestBody.securityLevel = securityLevel?.trim() || null
    if (iceVoyage !== undefined) requestBody.iceVoyage = iceVoyage
    if (eca !== undefined) requestBody.eca = eca
    if (etd?.trim()) requestBody.etd = etd.trim()
    if (cosp?.trim()) requestBody.cosp = cosp.trim()
    if (eosp?.trim()) requestBody.eosp = eosp.trim()
    if (arrivalFew?.trim()) requestBody.arrivalFew = arrivalFew.trim()
    if (eta?.trim()) requestBody.eta = eta.trim()
    if (estimatedDistance && !isNaN(parseFloat(estimatedDistance))) requestBody.estimatedDistance = parseFloat(estimatedDistance)
    if (displacement && !isNaN(parseFloat(displacement))) requestBody.displacement = parseFloat(displacement)
    if (draftFore && !isNaN(parseFloat(draftFore))) requestBody.draftFore = parseFloat(draftFore)
    if (draftMid && !isNaN(parseFloat(draftMid))) requestBody.draftMid = parseFloat(draftMid)
    if (draftAft && !isNaN(parseFloat(draftAft))) requestBody.draftAft = parseFloat(draftAft)
    if (totalCargoOnboard && !isNaN(parseFloat(totalCargoOnboard))) requestBody.totalCargoOnboard = parseFloat(totalCargoOnboard)
    if (loadingCondition !== undefined) requestBody.loadingCondition = loadingCondition?.trim() || null
    if (chartererName !== undefined) requestBody.chartererName = chartererName?.trim() || null
    if (chartererNo !== undefined) requestBody.chartererNo = chartererNo?.trim() || null
    if (cpSpeed && !isNaN(parseFloat(cpSpeed))) requestBody.cpSpeed = parseFloat(cpSpeed)
    if (cpConsumptionTotal && !isNaN(parseFloat(cpConsumptionTotal))) requestBody.cpConsumptionTotal = parseFloat(cpConsumptionTotal)
    if (agentName !== undefined) requestBody.agentName = agentName?.trim() || null
    if (agentContactNumber !== undefined) requestBody.agentContactNumber = agentContactNumber?.trim() || null
    if (agentTelephoneNumber !== undefined) requestBody.agentTelephoneNumber = agentTelephoneNumber?.trim() || null
    if (agentFaxAddress !== undefined) requestBody.agentFaxAddress = agentFaxAddress?.trim() || null
    if (agentEmail !== undefined) requestBody.agentEmail = agentEmail?.trim() || null

    const response = await axios.put(`${VOYAGES_API_URL}/update/${voyageId}`, requestBody)

    return response.data
  } catch (error: any) {
    console.error('Error updating voyage:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating voyage'))
  }
}

export const deleteVoyage = async (voyageId: number): Promise<Voyage[]> => {
  try {
    const response = await axios.delete(VOYAGES_API_URL + '/delete/' + voyageId)

    return response.data
  } catch (error: any) {
    console.error('Error deleting voyage item:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting voyage item'))
  }
}

export const getVoyageReportCount = async (voyageId: number): Promise<number> => {
  try {
    const response = await axios.get(`${VOYAGES_API_URL}/${voyageId}/reports/count`)

    return Number(response.data?.count ?? 0)
  } catch (error: any) {
    console.error('Error fetching voyage report count:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching voyage report count'))
  }
}

export const deleteVoyageReports = async (voyageId: number): Promise<void> => {
  try {
    await axios.delete(`${VOYAGES_API_URL}/${voyageId}/reports`)
  } catch (error: any) {
    console.error('Error deleting voyage reports:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting voyage reports'))
  }
}

export interface VoyageReportSummary {
  id: number
  reportType: string | null
  status: string | null
  createdDateTime: string | null
  submittedDateTime: string | null
}

export const getVoyageReports = async (voyageId: number): Promise<VoyageReportSummary[]> => {
  try {
    const response = await axios.get(`${VOYAGES_API_URL}/${voyageId}/reports`)
    return response.data ?? []
  } catch (error: any) {
    console.error('Error fetching voyage reports:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching voyage reports'))
  }
}

export const deleteSingleReport = async (reportId: number): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/reports/delete/${reportId}`)
  } catch (error: any) {
    console.error('Error deleting report:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting report'))
  }
}

export const startVoyage = async (voyageID: number): Promise<Voyage> => {
  try {
    const response = await axios.post(`${VOYAGES_API_URL}/${voyageID}/start`)

    return response.data
  } catch (error: any) {
    console.error('Failed to start voyage:', error)
    throw new Error(getApiErrorMessage(error, 'Failed to start voyage'))
  }
}

export const endVoyage = async (voyageID: number): Promise<Voyage> => {
  try {
    const response = await axios.post(`${VOYAGES_API_URL}/${voyageID}/end`)

    return response.data
  } catch (error: any) {
    console.error('Failed to end voyage:', error)
    throw new Error(getApiErrorMessage(error, 'Failed to end voyage'))
  }
}

export const getActiveVoyageForVessel = async (vesselId: number): Promise<Voyage[]> => {
  try {
    const response = await axios.get(`${VOYAGES_API_URL}/vessel/${vesselId}`)

    return response.data
  } catch (error: any) {
    console.error('Failed to load active voyage:', error)
    throw new Error(getApiErrorMessage(error, 'Failed to load active voyage'))
  }
}

export const getConsumptionROBData = async (
  accessToken: string,
  voyageID: number
): Promise<any> => {
  try {
    const response = await axios.get(`${API_URL}/consumption/user-data`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        voyageId: voyageID,
      },
    })

    return response.data
  } catch (error: any) {
    console.error('Error fetching consumption ROB data:', error)
    throw new Error('Failed to fetch consumption data')
  }
}

export const getConsumptionByVoyageReport = async (
  voyageId: number,
  reportId: number
): Promise<any> => {
  try {
    const response = await axios.get(
      `${API_URL}/consumption/voyages/${voyageId}/reports/${reportId}`
    )
    if (response.status === 204) return null
    return response.data
  } catch (error: any) {
    console.error('Error fetching consumption by voyage/report:', error)
    throw new Error('Failed to fetch consumption data')
  }
}

export interface CiiVoyageRowDto {
  id?: number
  voyageId: number
  legId?: number
  reportId: number
  vesselId?: number
  stsVoyage?: string
  fcVoyageHsfo?: string | number
  fcVoyageMgo?: string | number
  boilerUsed?: string
  hsfoSulphur?: string | number
  mgoSulphur?: string | number
  distanceAdjustment?: string | number
  reeferKwh?: string | number
  reeferSfoc?: string | number
  reeferFuelCode?: string
  remarks?: string
  fuelDetails?: CiiVoyageFuelDetailDto[]
}

export interface CiiVoyageFuelDetailDto {
  id?: number
  fuelCode: string
  fcVoyage?: string | number
  avgSulphur?: string | number
  fcElectrical?: string | number
  fcBoiler?: string | number
  fcOther?: string | number
  fcVoyageAdjustment?: string | number
}

export const upsertCiiVoyageRow = async (
  payload: CiiVoyageRowDto
): Promise<CiiVoyageRowDto> => {
  try {
    const response = await axios.post(`${API_URL}/cii-voyage-rows`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error saving CII voyage row:', error)
    throw new Error(getApiErrorMessage(error, 'Error saving CII voyage row'))
  }
}

export const getCiiVoyageRowsByReportIds = async (
  reportIds: number[]
): Promise<CiiVoyageRowDto[]> => {
  try {
    if (!reportIds || reportIds.length === 0) return []
    const response = await axios.post(`${API_URL}/cii-voyage-rows/by-report-ids`, {reportIds})
    return Array.isArray(response.data) ? response.data : []
  } catch (error: any) {
    console.error('Error fetching CII voyage rows:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching CII voyage rows'))
  }
}

export interface CiiShipTypeRuleDto {
  id?: number
  shipTypeCode: string
  displayName: string
  bandKey: string
  bandLabel?: string
  capacityBasis: 'DWT' | 'GT' | 'FIXED' | string
  minDwt?: number
  maxDwt?: number
  fixedCapacity?: number
  referenceA: number
  referenceC: number
  ratingD1: number
  ratingD2: number
  ratingD3: number
  ratingD4: number
  active?: boolean
}

export interface CiiFuelFactorDto {
  id?: number
  fuelCode: string
  displayName: string
  aliases?: string
  co2Factor: number
  pmFactor?: number
  active?: boolean
}

export interface CiiReductionFactorDto {
  id?: number
  reportingYear: number
  reductionPercent: number
  correctionYearIndex: number
  active?: boolean
}

export interface CiiRuleSetDto {
  shipTypeRules: CiiShipTypeRuleDto[]
  fuelFactors: CiiFuelFactorDto[]
  reductionFactors: CiiReductionFactorDto[]
}

export interface CiiVesselSettingDto {
  id?: number
  vesselId?: number
  shipTypeCode: string
  capacityOverride?: number | string
  factorFi?: number | string
  factorFm?: number | string
  factorFc?: number | string
  factorFiVse?: number | string
  updatedBy?: number
  updatedAt?: string
}

export const getCiiRuleSet = async (): Promise<CiiRuleSetDto> => {
  try {
    const response = await axios.get(`${API_URL}/cii-rules/rule-set`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching CII rule set:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching CII rule set'))
  }
}

export const getCiiVesselSetting = async (vesselId: number): Promise<CiiVesselSettingDto | null> => {
  try {
    const response = await axios.get(`${API_URL}/cii-rules/vessels/${vesselId}/settings`)
    return response.status === 204 ? null : response.data
  } catch (error: any) {
    console.error('Error fetching CII vessel setting:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching CII vessel setting'))
  }
}

export const saveCiiVesselSetting = async (
  vesselId: number,
  payload: CiiVesselSettingDto
): Promise<CiiVesselSettingDto> => {
  try {
    const response = await axios.post(`${API_URL}/cii-rules/vessels/${vesselId}/settings`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error saving CII vessel setting:', error)
    throw new Error(getApiErrorMessage(error, 'Error saving CII vessel setting'))
  }
}

export const upsertCiiShipTypeRule = async (
  payload: CiiShipTypeRuleDto
): Promise<CiiShipTypeRuleDto> => {
  try {
    const response = await axios.post(`${API_URL}/cii-rules/ship-type-rules`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error saving CII ship type rule:', error)
    throw new Error(getApiErrorMessage(error, 'Error saving CII ship type rule'))
  }
}

export const upsertCiiFuelFactor = async (
  payload: CiiFuelFactorDto
): Promise<CiiFuelFactorDto> => {
  try {
    const response = await axios.post(`${API_URL}/cii-rules/fuel-factors`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error saving CII fuel factor:', error)
    throw new Error(getApiErrorMessage(error, 'Error saving CII fuel factor'))
  }
}

export const upsertCiiReductionFactor = async (
  payload: CiiReductionFactorDto
): Promise<CiiReductionFactorDto> => {
  try {
    const response = await axios.post(`${API_URL}/cii-rules/reduction-factors`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error saving CII reduction factor:', error)
    throw new Error(getApiErrorMessage(error, 'Error saving CII reduction factor'))
  }
}

//Integration for new position report
// —–––– Vessels APIs ––––

export const getVesselList = async (): Promise<Vessel[]> => {
  try {
    const response = await axios.get(VESSEL_API_URL, {
      headers: {},
    })

    return response.data
  } catch (error: any) {
    console.error('Error fetching vessel list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching vessel list')
  }
}

export const getCompanyAdminList = async (): Promise<CompanyAdmin[]> => {
  try {
    const response = await axios.get(COMPANY_ADMIN_API_URL)

    return response.data // Return company data
  } catch (error: any) {
    console.error('Error fetching company admin list:', error)
    throw new Error(error.response?.data?.message || 'Error fetching company admin list')
  }
}

// Latest-by-day reports (Position Report - new)
export const getLatestReportsByDay = async (
  fromISO: string, // 'YYYY-MM-DD'
  toISO: string, // same as fromISO (one day at a time)
  vesselId: number
): Promise<any[]> => {
  try {
    const response = await axios.get(`${API_URL}/reports/latest-by-day`, {
      params: {from: fromISO, to: toISO, vesselId},
    })
    return response.data // array; we'll read [0]?.values
  } catch (error: any) {
    console.error('Error fetching latest-by-day reports:', error)
    throw new Error(error?.response?.data?.message || 'Error fetching latest-by-day reports')
  }
}

export const getSubmittedReportsFull = async (
  fromISO: string,
  toISO: string,
  vesselId: number
): Promise<any[]> => {
  try {
    const response = await axios.get(`${API_URL}/reports/submitted-full`, {
      params: {from: fromISO, to: toISO, vesselId},
    })
    return response.data
  } catch (error: any) {
    console.error('Error fetching submitted full reports:', error)
    throw new Error(error?.response?.data?.message || 'Error fetching submitted full reports')
  }
}
export interface ConsumptionLubeOilRow {
  grade?: string
  openingStock?: string
  received?: string
  consumedByVessel?: string
  closingStock?: string
}

export interface ConsumptionRequestDTO {
  reportId?: number
  userId?: string | number
  companyId?: string | number
  voyageId?: number
  remarks?: string
  rows?: {
    fieldType?: string
    initialRob?: number
    lastRob?: number
    machineries?: {machinery?: string; operationType?: string; consumed?: number; rob?: number}[]
  }[]
  lubeOils?: ConsumptionLubeOilRow[]
}

export const submitConsumptionROB = async (token: string, dto: ConsumptionRequestDTO) => {
  const response = await fetch(`${API_URL}/consumption/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(dto),
  })

  const data = await response.json().catch(() => null)
  if (!response.ok) {
    throw new Error(data?.message || 'Failed to save Consumption & ROB.')
  }

  return data
}

export const getConsumptionFuelTypes = async (token?: string): Promise<string[]> => {
  const res = await axios.get(`${API_URL}/consumption/fuel-types`, {
    headers: token ? {Authorization: `Bearer ${token}`} : undefined,
  })
  return res.data
}

export const getConsumptionMachineryTypes = async (token?: string): Promise<string[]> => {
  const res = await axios.get(`${API_URL}/consumption/machinery-types`, {
    headers: token ? {Authorization: `Bearer ${token}`} : undefined,
  })
  return res.data
}

export const getConsumptionByVoyage = async (token: string = '', voyageId: number) => {
  let activeToken = token
  if (!activeToken && typeof window !== 'undefined') {
    try {
      const ls = window.localStorage?.getItem('kt-auth-react-v')
      if (ls) {
        const parsed = JSON.parse(ls)
        activeToken = parsed?.auth?.jwt || ''
      }
    } catch {}
  }
  const headers = activeToken ? {Authorization: `Bearer ${activeToken}`} : undefined
  const res = await axios.get(`${API_URL}/consumption/voyage/${voyageId}`, {
    headers,
  })
  return res.data
}

// ✅ Correct version of user-data (if you still need it)
export const getConsumptionUserData = async (token: string, userId: string, companyId: string) => {
  const res = await axios.get(`${API_URL}/consumption/user-data`, {
    headers: {Authorization: `Bearer ${token}`},
    params: {userId, companyId},
  })
  return res.data
}

// GET /vessels/operations/{vesselId}
export const getVesselOperations = async (vesselId: number): Promise<VesselOperations> => {
  try {
    const response = await axios.get(`${VESSEL_API_URL}/operations/${vesselId}`)
    return response.data as VesselOperations
  } catch (error: any) {
    console.error('Error fetching vessel operations:', error)
    throw new Error(error?.response?.data?.message || 'Error fetching vessel operations')
  }
}

// === Cargo Operations APIs ===
const CARGO_OPERATIONS_API_URL = `${API_URL}/cargo-operations`

export interface CargoOperationFilters {
  companyId?: number | null
  vesselId?: number | null
  voyageId?: number | null
  legId?: number | null
  breakupType?: CargoBreakupType | null
  status?: CargoOperationStatus | string | null
  fromDate?: string | null
  toDate?: string | null
  searchText?: string | null
  page?: number
  size?: number
}

export interface CargoOperationPageResponse {
  content: CargoOperation[]
  totalElements: number
  totalPages: number
  size: number
  number: number
}

export const getCargoOperations = async (
  filters: CargoOperationFilters = {}
): Promise<CargoOperationPageResponse> => {
  try {
    const params: any = {}
    if (filters.companyId) params.companyId = filters.companyId
    if (filters.vesselId) params.vesselId = filters.vesselId
    if (filters.voyageId) params.voyageId = filters.voyageId
    if (filters.legId) params.legId = filters.legId
    if (filters.breakupType) params.breakupType = filters.breakupType
    if (filters.status) params.status = filters.status
    if (filters.fromDate) params.fromDate = filters.fromDate
    if (filters.toDate) params.toDate = filters.toDate
    if (filters.searchText) params.searchText = filters.searchText
    if (filters.page !== undefined) params.page = filters.page
    if (filters.size !== undefined) params.size = filters.size

    const response = await axios.get(CARGO_OPERATIONS_API_URL, {params})
    return response.data
  } catch (error: any) {
    console.error('Error fetching cargo operations:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching cargo operations'))
  }
}

export const getCargoOperationById = async (id: number): Promise<CargoOperation> => {
  try {
    const response = await axios.get(`${CARGO_OPERATIONS_API_URL}/${id}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching cargo operation:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching cargo operation'))
  }
}

export const createCargoOperation = async (
  payload: CargoOperationRequest
): Promise<CargoOperation> => {
  try {
    const response = await axios.post(CARGO_OPERATIONS_API_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating cargo operation:', error)
    throw new Error(getApiErrorMessage(error, 'Error creating cargo operation'))
  }
}

export const updateCargoOperation = async (
  id: number,
  payload: CargoOperationRequest
): Promise<CargoOperation> => {
  try {
    const response = await axios.put(`${CARGO_OPERATIONS_API_URL}/${id}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating cargo operation:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating cargo operation'))
  }
}

export const submitCargoOperation = async (id: number): Promise<CargoOperation> => {
  try {
    const response = await axios.post(`${CARGO_OPERATIONS_API_URL}/${id}/submit`)
    return response.data
  } catch (error: any) {
    console.error('Error submitting cargo operation for approval:', error)
    throw new Error(getApiErrorMessage(error, 'Error submitting cargo operation for approval'))
  }
}

export const approveCargoOperation = async (
  id: number,
  comments?: string
): Promise<CargoOperation> => {
  try {
    const response = await axios.post(`${CARGO_OPERATIONS_API_URL}/${id}/approve`, {comments})
    return response.data
  } catch (error: any) {
    console.error('Error approving cargo operation:', error)
    throw new Error(getApiErrorMessage(error, 'Error approving cargo operation'))
  }
}

export const rejectCargoOperation = async (
  id: number,
  comments: string
): Promise<CargoOperation> => {
  try {
    const response = await axios.post(`${CARGO_OPERATIONS_API_URL}/${id}/reject`, {comments})
    return response.data
  } catch (error: any) {
    console.error('Error rejecting cargo operation:', error)
    throw new Error(getApiErrorMessage(error, 'Error rejecting cargo operation'))
  }
}

export const deleteCargoOperation = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${CARGO_OPERATIONS_API_URL}/${id}`)
  } catch (error: any) {
    console.error('Error deleting cargo operation:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting cargo operation'))
  }
}

export const uploadCargoFinalAttachment = async (
  cargoOperationId: number,
  documentName: string,
  remarks: string | null,
  file: File
): Promise<CargoFinalAttachment> => {
  try {
    const formData = new FormData()
    formData.append('documentName', documentName)
    if (remarks) {
      formData.append('remarks', remarks)
    }
    formData.append('file', file)

    const response = await axios.post<CargoFinalAttachment>(
      `${CARGO_OPERATIONS_API_URL}/${cargoOperationId}/final-attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  } catch (error: any) {
    console.error('Error uploading cargo final attachment:', error)
    throw new Error(getApiErrorMessage(error, 'Error uploading cargo final attachment'))
  }
}

export const deleteCargoFinalAttachment = async (
  cargoOperationId: number,
  attachmentId: number
): Promise<void> => {
  try {
    await axios.delete(
      `${CARGO_OPERATIONS_API_URL}/${cargoOperationId}/final-attachments/${attachmentId}`
    )
  } catch (error: any) {
    console.error('Error deleting cargo final attachment:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting cargo final attachment'))
  }
}

export const updateCargoFinalAttachmentRemarks = async (
  cargoOperationId: number,
  attachmentId: number,
  remarks: string
): Promise<CargoFinalAttachment> => {
  try {
    const response = await axios.patch<CargoFinalAttachment>(
      `${CARGO_OPERATIONS_API_URL}/${cargoOperationId}/final-attachments/${attachmentId}`,
      {remarks}
    )
    return response.data
  } catch (error: any) {
    console.error('Error updating cargo final attachment remarks:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating cargo final attachment remarks'))
  }
}

export const uploadCargoDetailAttachment = async (
  cargoOperationId: number,
  orderIndex: number,
  file: File
): Promise<void> => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    await axios.post(
      `${CARGO_OPERATIONS_API_URL}/${cargoOperationId}/details/${orderIndex}/attachment`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
  } catch (error: any) {
    console.error('Error uploading cargo detail attachment:', error)
    throw new Error(getApiErrorMessage(error, 'Error uploading cargo detail attachment'))
  }
}

// View / download detail attachment URLs
export const cargoDetailAttachmentViewUrl = (cargoOperationId: number, orderIndex: number) =>
  `${CARGO_OPERATIONS_API_URL}/${cargoOperationId}/details/${orderIndex}/attachment/view`

export const cargoDetailAttachmentDownloadUrl = (cargoOperationId: number, orderIndex: number) =>
  `${CARGO_OPERATIONS_API_URL}/${cargoOperationId}/details/${orderIndex}/attachment/download`

// View / download final attachment URLs
export const cargoFinalAttachmentViewUrl = (cargoOperationId: number, attachmentId: number) =>
  `${CARGO_OPERATIONS_API_URL}/${cargoOperationId}/final-attachments/${attachmentId}/view`

export const cargoFinalAttachmentDownloadUrl = (cargoOperationId: number, attachmentId: number) =>
  `${CARGO_OPERATIONS_API_URL}/${cargoOperationId}/final-attachments/${attachmentId}/download`

// === Daily Position Report (DPR) APIs ===
const DPR_API_URL = `${API_URL}/dpr`

export const getDprList = async (): Promise<any[]> => {
  try {
    const response = await axios.get(DPR_API_URL)
    return response.data
  } catch (error: any) {
    console.error('Error fetching DPR list:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching DPR list'))
  }
}

export const getDprById = async (id: number): Promise<any> => {
  try {
    const response = await axios.get(`${DPR_API_URL}/${id}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching DPR:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching DPR'))
  }
}

export const createDpr = async (payload: any): Promise<any> => {
  try {
    const response = await axios.post(DPR_API_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating DPR:', error)
    throw new Error(getApiErrorMessage(error, 'Error creating DPR'))
  }
}

export const updateDpr = async (id: number, payload: any): Promise<any> => {
  try {
    const response = await axios.put(`${DPR_API_URL}/${id}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating DPR:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating DPR'))
  }
}

export const deleteDpr = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${DPR_API_URL}/${id}`)
  } catch (error: any) {
    console.error('Error deleting DPR:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting DPR'))
  }
}

export const patchDprSection = async (
  id: number,
  sectionName: string,
  patchPayload: any
): Promise<any> => {
  try {
    const response = await axios.patch(`${DPR_API_URL}/${id}/section/${sectionName}`, patchPayload)
    return response.data
  } catch (error: any) {
    console.error(`Error patching DPR section ${sectionName}:`, error)
    throw new Error(getApiErrorMessage(error, `Error auto-saving section ${sectionName}`))
  }
}

export const acquireDprSectionLease = async (id: number, sectionName: string): Promise<any> => {
  try {
    const response = await axios.post(`${DPR_API_URL}/${id}/lease`, null, {
      params: { sectionName },
    })
    return response.data
  } catch (error: any) {
    console.error(`Error acquiring lease for section ${sectionName}:`, error)
    throw error
  }
}

export const releaseDprSectionLease = async (id: number, sectionName: string): Promise<void> => {
  try {
    await axios.delete(`${DPR_API_URL}/${id}/lease`, {
      params: { sectionName },
    })
  } catch (error: any) {
    console.error(`Error releasing lease for section ${sectionName}:`, error)
  }
}

export const getDprActiveLeases = async (id: number): Promise<any[]> => {
  try {
    const response = await axios.get(`${DPR_API_URL}/${id}/leases`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching DPR active leases:', error)
    return []
  }
}

export const getDprChangeHistory = async (
  id: number,
  sectionName?: string,
  page: number = 0,
  size: number = 50
): Promise<any> => {
  try {
    const params: any = { page, size }
    if (sectionName) params.sectionName = sectionName
    const response = await axios.get(`${DPR_API_URL}/${id}/history`, { params })
    return response.data
  } catch (error: any) {
    console.error('Error fetching DPR change history:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching change history'))
  }
}

/** Fetch unresolved Continue movement records for a vessel (cross-DPR tracking) */
export const getUnresolvedContinue = async (vesselId: number): Promise<any> => {
  try {
    const response = await axios.get(`${DPR_API_URL}/unresolved-continue`, {
      params: { vesselId },
    })
    return response.data
  } catch (error: any) {
    console.error('Error fetching unresolved continue:', error)
    return null // graceful fallback — feature works without backend support
  }
}

// ──────────────────────────── DMR ────────────────────────────
const DMR_API_URL = `${API_URL}/daily-movement-reports`

export const getDmrList = async (vesselId?: number): Promise<any[]> => {
  try {
    const params = new URLSearchParams()
    appendIdParam(params, 'vesselId', vesselId)
    const response = await axios.get(DMR_API_URL, {
      params: params.toString() ? params : undefined,
    })
    return response.data
  } catch (error: any) {
    console.error('Error fetching DMR list:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching DMR list'))
  }
}

export const getDmrById = async (id: number): Promise<any> => {
  try {
    const response = await axios.get(`${DMR_API_URL}/${id}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching DMR:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching DMR'))
  }
}

export const createDmr = async (payload: any): Promise<any> => {
  try {
    const response = await axios.post(DMR_API_URL, payload)
    return response.data
  } catch (error: any) {
    console.error('Error creating DMR:', error)
    throw new Error(getApiErrorMessage(error, 'Error creating DMR'))
  }
}

export const updateDmr = async (id: number, payload: any): Promise<any> => {
  try {
    const response = await axios.put(`${DMR_API_URL}/${id}`, payload)
    return response.data
  } catch (error: any) {
    console.error('Error updating DMR:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating DMR'))
  }
}

export const deleteDmr = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${DMR_API_URL}/${id}`)
  } catch (error: any) {
    console.error('Error deleting DMR:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting DMR'))
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Leg API — Voyage & Leg Re-Architecture
// Base URL: /api/voyages/{voyageId}/legs
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch all active legs for a voyage, ordered by sequence number.
 */
export const getLegsByVoyage = async (voyageId: number): Promise<Leg[]> => {
  try {
    const response = await axios.get(`${VOYAGES_API_URL}/${voyageId}/legs`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching legs:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching legs'))
  }
}

/**
 * Fetch a single leg by ID within a voyage.
 */
export const getLegById = async (voyageId: number, legId: number): Promise<Leg> => {
  try {
    const response = await axios.get(`${VOYAGES_API_URL}/${voyageId}/legs/${legId}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching leg:', error)
    throw new Error(getApiErrorMessage(error, 'Error fetching leg'))
  }
}

/**
 * Create a new leg within a voyage.
 * Sequence number is auto-assigned by the server.
 * departurePort is required; arrivalPort is optional.
 */
export const createLeg = async (voyageId: number, legData: Partial<Leg>): Promise<Leg> => {
  try {
    const response = await axios.post(`${VOYAGES_API_URL}/${voyageId}/legs`, legData)
    return response.data
  } catch (error: any) {
    console.error('Error creating leg:', error)
    throw new Error(getApiErrorMessage(error, 'Error creating leg'))
  }
}

/**
 * Update an existing leg's details.
 */
export const updateLeg = async (voyageId: number, legId: number, legData: Partial<Leg>): Promise<Leg> => {
  try {
    const response = await axios.put(`${VOYAGES_API_URL}/${voyageId}/legs/${legId}`, legData)
    return response.data
  } catch (error: any) {
    console.error('Error updating leg:', error)
    throw new Error(getApiErrorMessage(error, 'Error updating leg'))
  }
}

/**
 * Soft-delete a leg.
 * Returns 409 Conflict if the leg has active reports.
 */
export const deleteLeg = async (voyageId: number, legId: number): Promise<void> => {
  try {
    await axios.delete(`${VOYAGES_API_URL}/${voyageId}/legs/${legId}`)
  } catch (error: any) {
    console.error('Error deleting leg:', error)
    throw new Error(getApiErrorMessage(error, 'Error deleting leg'))
  }
}

/**
 * Start a leg — marks it as active/started.
 * Returns 409 Conflict if another leg in the same voyage is already active.
 */
export const startLeg = async (voyageId: number, legId: number): Promise<Leg> => {
  try {
    const response = await axios.post(`${VOYAGES_API_URL}/${voyageId}/legs/${legId}/start`)
    return response.data
  } catch (error: any) {
    console.error('Error starting leg:', error)
    throw new Error(getApiErrorMessage(error, 'Error starting leg'))
  }
}

/**
 * End a leg — marks it as completed.
 * Also propagates the leg's endDate and arrivalPort to the parent voyage.
 */
export const endLeg = async (voyageId: number, legId: number): Promise<Leg> => {
  try {
    const response = await axios.post(`${VOYAGES_API_URL}/${voyageId}/legs/${legId}/end`)
    return response.data
  } catch (error: any) {
    console.error('Error ending leg:', error)
    throw new Error(getApiErrorMessage(error, 'Error ending leg'))
  }
}
